// BackendTemplateConfig.js
// Centralized configuration for backend template styling

const BackendTemplateConfig = {
  // Container styles
  container: {
    minHeight: 'min-h-screen',
    backgroundColor: 'bg-gray-100',
    backgroundGradient: 'bg-gradient-to-br from-blue-50 to-indigo-100',
    padding: 'p-4',
    paddingLarge: 'p-6',
    maxWidth: 'max-w-7xl',
    marginX: 'mx-auto'
  },
  
  // Typography
  typography: {
    pageTitle: 'text-3xl font-bold text-gray-800',
    sectionTitle: 'text-xl font-bold text-gray-800',
    subTitle: 'text-xl font-semibold text-gray-700',
    heading: 'font-semibold text-gray-800',
    label: 'text-sm font-semibold text-gray-700',
    body: 'text-sm text-gray-600',
    small: 'text-xs text-gray-500',
    error: 'text-red-600',
    info: 'text-blue-600',
    muted: 'text-gray-500',
    // User specific
    userName: 'text-sm font-medium text-gray-900 truncate',
    userEmail: 'text-sm text-gray-500 truncate',
    userId: 'text-sm text-gray-500 truncate',
    userInfo: 'text-sm text-gray-500',
    statusText: 'text-sm font-medium text-gray-900',
    lastActiveText: 'text-sm text-gray-500',
    activeNowText: 'text-xs text-green-600 font-medium',
    awayText: 'text-xs text-yellow-600 font-medium'
  },
  
  // Card styles
  card: {
    base: 'bg-white rounded-2xl shadow-xl overflow-hidden',
    simple: 'bg-white rounded-lg shadow-sm',
    hover: 'bg-white hover:bg-gray-50 transition-all rounded-lg cursor-pointer group',
    padding: 'p-4',
    paddingLarge: 'p-6',
    spacing: 'space-y-2',
    spacingLarge: 'space-y-3'
  },
  
  // Calendar specific
  calendar: {
    wrapper: 'bg-white rounded-2xl shadow-xl overflow-hidden',
    mainSection: 'flex-1 p-6',
    sidePanel: 'lg:w-80 bg-gray-50 p-6 border-t lg:border-t-0 lg:border-l border-gray-200',
    headerRow: 'flex items-center justify-between mb-6',
    monthSelector: 'flex items-center gap-4',
    monthLabel: 'text-xl font-semibold text-gray-700 min-w-[150px] text-center',
    weekDayHeader: 'text-center font-semibold text-gray-600 py-2',
    grid: 'grid grid-cols-7 gap-0 border-t border-l border-gray-200',
    dayCell: {
      base: 'h-24 border border-gray-200 p-2 cursor-pointer transition-all hover:bg-gray-50',
      empty: 'h-24 border border-gray-100',
      selected: 'bg-blue-50 border-blue-400',
      today: 'bg-yellow-50'
    },
    dayNumber: {
      default: 'font-semibold text-sm mb-1 text-gray-700',
      today: 'font-semibold text-sm mb-1 text-blue-600'
    },
    event: {
      chip: 'text-xs px-1 py-0.5 rounded text-white truncate',
      more: 'text-xs text-gray-500'
    },
    glossary: {
      wrapper: 'bg-gray-100 px-6 py-4 border-t border-gray-200',
      item: 'flex items-center gap-2',
      indicator: 'w-4 h-4 rounded'
    },
    statsSection: 'mt-6 pt-6 border-t border-gray-300',
    emptyState: 'text-gray-500 text-center py-8'
  },
  
  // Event types colors
  eventTypes: {
    meeting: 'bg-blue-500',
    deadline: 'bg-red-500',
    personal: 'bg-green-500',
    holiday: 'bg-purple-500'
  },
  
  // Avatar styles
  avatar: {
    container: 'w-16 h-16 flex-shrink-0 bg-gray-50 rounded-full overflow-hidden',
    inner: 'w-full h-full flex items-center justify-center',
    icon: 'h-8 w-8 text-gray-300'
  },
  
  // Status indicator
  statusIndicator: {
    base: 'w-2.5 h-2.5 rounded-full flex-shrink-0 ring-1 ring-white',
    colors: {
      online: 'bg-green-500',
      away: 'bg-yellow-500',
      offline: 'bg-gray-400'
    }
  },
  
  // Buttons
  buttons: {
    primary: 'px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors',
    secondary: 'px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors',
    ghost: 'p-2 hover:bg-gray-100 rounded-lg transition-colors',
    icon: {
      base: 'p-2 rounded-lg transition-colors',
      variants: {
        default: 'text-gray-600 hover:bg-gray-100',
        calendar: 'text-gray-600 hover:text-blue-600 hover:bg-blue-50',
        edit: 'text-gray-600 hover:text-green-600 hover:bg-green-50',
        view: 'text-gray-600 hover:text-purple-600 hover:bg-purple-50'
      }
    }
  },
  
  // Icons
  icons: {
    small: 'w-3 h-3',
    medium: 'w-4 h-4',
    normal: 'w-5 h-5',
    large: 'w-8 h-8'
  },
  
  // Tooltip styles
  tooltip: {
    container: 'absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover/tooltip:opacity-100 transition-opacity pointer-events-none',
    arrow: 'absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 w-0 h-0 border-4 border-transparent border-t-gray-900'
  },
  
  // Layout utilities
  layout: {
    flex: 'flex',
    flexCol: 'flex flex-col',
    flexRow: 'flex flex-row',
    flexBetween: 'flex items-center justify-between',
    flexCenter: 'flex items-center justify-center',
    flexStart: 'flex items-start',
    flexGap: {
      small: 'gap-1',
      medium: 'gap-2',
      large: 'gap-3',
      xlarge: 'gap-4'
    },
    // User list specific
    userRow: 'flex items-center gap-4',
    userDetails: 'flex-1 min-w-0',
    userNameRow: 'flex items-center gap-2 mb-1',
    statusColumn: 'flex flex-col items-end gap-1 flex-shrink-0 mr-4',
    actionButtonsGroup: 'flex items-center gap-2 flex-shrink-0'
  },
  
  // States
  states: {
    loading: 'text-center py-4 text-blue-600',
    error: 'text-center py-4 text-red-600 bg-red-50 rounded-lg mb-4',
    empty: 'text-gray-500 text-center py-8',
    disabled: 'opacity-50 cursor-not-allowed'
  },
  
  // Color palette
  colors: {
    primary: {
      50: 'bg-blue-50',
      100: 'bg-blue-100',
      500: 'bg-blue-500',
      600: 'bg-blue-600',
      700: 'bg-blue-700',
      text: 'text-blue-600',
      border: 'border-blue-400'
    },
    success: {
      50: 'bg-green-50',
      500: 'bg-green-500',
      600: 'bg-green-600',
      text: 'text-green-600'
    },
    warning: {
      50: 'bg-yellow-50',
      500: 'bg-yellow-500',
      600: 'bg-yellow-600',
      text: 'text-yellow-600'
    },
    danger: {
      50: 'bg-red-50',
      500: 'bg-red-500',
      600: 'bg-red-600',
      text: 'text-red-600'
    },
    neutral: {
      50: 'bg-gray-50',
      100: 'bg-gray-100',
      200: 'bg-gray-200',
      300: 'bg-gray-300',
      400: 'bg-gray-400',
      500: 'bg-gray-500',
      600: 'bg-gray-600',
      700: 'bg-gray-700',
      800: 'bg-gray-800',
      900: 'bg-gray-900',
      border: {
        light: 'border-gray-100',
        default: 'border-gray-200',
        dark: 'border-gray-300'
      },
      text: {
        primary: 'text-gray-900',
        secondary: 'text-gray-700',
        tertiary: 'text-gray-600',
        muted: 'text-gray-500',
        light: 'text-gray-400'
      }
    },
    white: 'bg-white',
    transparent: 'bg-transparent'
  },
  
  // Spacing presets
  spacing: {
    xs: 'gap-1',
    sm: 'gap-2',
    md: 'gap-4',
    lg: 'gap-6',
    xl: 'gap-8',
    none: 'gap-0'
  },
  
  // Border radius presets
  borderRadius: {
    none: 'rounded-none',
    sm: 'rounded',
    md: 'rounded-lg',
    lg: 'rounded-xl',
    xl: 'rounded-2xl',
    full: 'rounded-full'
  },
  
  // Shadow presets
  shadows: {
    none: 'shadow-none',
    sm: 'shadow-sm',
    md: 'shadow-md',
    lg: 'shadow-lg',
    xl: 'shadow-xl'
  },
  
  // Border widths
  borders: {
    none: 'border-0',
    default: 'border',
    top: 'border-t',
    bottom: 'border-b',
    left: 'border-l',
    right: 'border-r'
  },
  
  // Animation presets
  animations: {
    transition: {
      all: 'transition-all',
      colors: 'transition-colors',
      opacity: 'transition-opacity',
      transform: 'transition-transform'
    },
    duration: {
      fast: 'duration-150',
      normal: 'duration-300',
      slow: 'duration-500'
    },
    hover: {
      scale: 'hover:scale-105',
      opacity: 'hover:opacity-80',
      shadow: 'hover:shadow-lg',
      bgGray: 'hover:bg-gray-50',
      bgGray100: 'hover:bg-gray-100'
    }
  },
  
  // Responsive utilities
  responsive: {
    hideMobile: 'hidden lg:block',
    hideDesktop: 'lg:hidden',
    stackMobile: 'flex-col lg:flex-row'
  }
};

export default BackendTemplateConfig;